import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { StyledCarousel } from '@ynap/carousel';
import { useAppState } from '@ynap/immutables-provider';
import { withOptimizely } from '@ynap/optimizely-utils';
import { upsellListImpression } from '../analytics';
import carouselConfig from './recommendationsCarouselHelper';
import RecommendationsProduct from './RecommendationsProduct';

const bem = new BEMHelper('RecommendationsProducts');

const RecommendationsProductList = ({
    addToCart,
    carouselButtons,
    carouselConfig: carouselConfigOverride,
    className,
    CTAButtonsState,
    doubleImageModifier,
    doubleImageWithoutPadding,
    handleOverlayProductIdChange,
    heading,
    isCarouselVisible,
    isFullWidth,
    isWishlistRecommendations,
    isWithAddToBag = false,
    messages,
    noDescription,
    onClick = () => {},
    onRemove,
    optimizely,
    overlayProductId,
    recommendations,
    reducedMode = false,
    setIsPhoneOverlay,
    tracking,
    type,
    useDefaultCarouselIndex,
    variation,
    viewSimilarAndShopMore = null,
    withQuickAdd = false,
}) => {
    const [lastAddedIndex, setLastAddedIndex] = useState(-1);
    const { addToCart: addToBagState = {} } = useAppState();
    const { loading: addToCartLoading } = addToBagState;
    const recommendationsVisible = recommendations && recommendations?.length > 0;
    const modifiers = [
        recommendationsVisible ? 'showRecommendations' : 'hideRecommendations',
        isFullWidth ? 'fullWidth' : '',
        withQuickAdd ? 'quickAdd' : '',
        reducedMode ? 'reducedMode' : '',
    ];

    const carouselClassNames = {
        prev: carouselButtons?.prev ?? bem('controls', 'arrow-prev'),
        next: carouselButtons?.next ?? bem('controls', 'arrow-next'),
        container: 'overlay',
        trackModifier: 'ymal',
        slideModifier: 'ymal',
    };

    useEffect(() => {
        if (recommendationsVisible) {
            tracking?.trackEvent?.(upsellListImpression(recommendations, type));
        }
    }, [type, recommendationsVisible]);

    useEffect(() => {
        if (!addToCartLoading && lastAddedIndex > -1) {
            const clearLastAdded = setTimeout(() => setLastAddedIndex(-1), 2000);
            return () => clearLastAdded && clearTimeout(clearLastAdded);
        }
    }, [addToCartLoading]);

    return (
        <div className={bem(null, modifiers, className)}>
            {recommendationsVisible && (
                <>
                    <div className={bem('topDivider', reducedMode ? 'reducedMode' : '')} />
                    <section className={bem('sectionWrapper', reducedMode && 'reducedMode')}>
                        {heading && <h3 className={bem('header', modifiers)}>{heading()}</h3>}
                        {isCarouselVisible ? (
                            <StyledCarousel
                                carouselClassNames={carouselClassNames}
                                carouselConfig={carouselConfig(carouselConfigOverride)}
                                slidesLength={recommendations.length}
                                useDefaultCarouselIndex={useDefaultCarouselIndex}
                                onNextClick={() => {
                                    /* istanbul ignore next */
                                    optimizely?.track('recommendations_carousel_control_click');
                                }}
                                onPrevClick={() => {
                                    /* istanbul ignore next */
                                    optimizely?.track('recommendations_carousel_control_click');
                                }}
                            >
                                {recommendations.map((recommendedProduct, index) => (
                                    <RecommendationsProduct
                                        key={recommendedProduct && recommendedProduct.href}
                                        details
                                        omnibusAlignment
                                        CTAButtonsState={CTAButtonsState}
                                        doubleImageModifier={doubleImageModifier}
                                        doubleImageWithoutPadding={doubleImageWithoutPadding}
                                        handleOverlayProductIdChange={handleOverlayProductIdChange}
                                        inProgress={lastAddedIndex > -1}
                                        index={index}
                                        isLastAdded={index === lastAddedIndex}
                                        isWishlistRecommendations={isWishlistRecommendations}
                                        isWithAddToBag={isWithAddToBag}
                                        messages={messages}
                                        modifier={noDescription ? 'descriptionStyle' : 'pdpRecommendations'}
                                        noDescription={noDescription}
                                        onClick={onClick}
                                        onRemove={onRemove}
                                        overlayProductId={overlayProductId}
                                        recommendedProduct={recommendedProduct}
                                        setIsPhoneOverlay={setIsPhoneOverlay}
                                        tracking={tracking}
                                        type={type}
                                        variation={variation}
                                        withQuickAdd={withQuickAdd}
                                        addToCart={(partNumber, { quantity }) => {
                                            setLastAddedIndex(index);
                                            return addToCart(partNumber, { quantity });
                                        }}
                                    />
                                ))}
                            </StyledCarousel>
                        ) : (
                            <>
                                <div className={bem('products')}>
                                    {recommendations.map((recommendedProduct, index) => (
                                        <RecommendationsProduct
                                            key={recommendedProduct && recommendedProduct.href}
                                            inProgress={lastAddedIndex > -1}
                                            index={index}
                                            isLastAdded={index === lastAddedIndex}
                                            isWithAddToBag={isWithAddToBag}
                                            messages={messages}
                                            modifier={noDescription ? 'descriptionStyle' : 'pdpRecommendations'}
                                            noDescription={noDescription}
                                            onClick={onClick}
                                            onRemove={onRemove}
                                            recommendedProduct={recommendedProduct}
                                            reducedMode={reducedMode}
                                            tracking={tracking}
                                            type={type}
                                            variation={variation}
                                            withQuickAdd={withQuickAdd}
                                            addToCart={(partNumber, { quantity }) => {
                                                setLastAddedIndex(index);
                                                return addToCart(partNumber, { quantity });
                                            }}
                                        />
                                    ))}
                                </div>
                                {viewSimilarAndShopMore}
                            </>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

RecommendationsProductList.propTypes = {
    recommendations: PropTypes.array,
    messages: PropTypes.shape({
        wearItWith: PropTypes.shape({
            heading: PropTypes.func.isRequired,
        }).isRequired,
        youMayAlsoLike: PropTypes.shape({
            heading: PropTypes.func.isRequired,
        }).isRequired,
    }),
    heading: PropTypes.string,
    className: PropTypes.string,
    type: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    isCarouselVisible: PropTypes.bool,
    isWithAddToBag: PropTypes.bool,
    addToBagState: PropTypes.object,
    addToCart: PropTypes.func,
    useDefaultCarouselIndex: PropTypes.bool,
    carouselConfig: PropTypes.object,
    tracking: PropTypes.func.isRequired,
    isFullWidth: PropTypes.bool,
    withQuickAdd: PropTypes.bool,
    variation: PropTypes.string,
    carouselButtons: PropTypes.shape({
        prev: PropTypes.any,
        next: PropTypes.any,
    }),
    optimizely: PropTypes.shape({
        track: PropTypes.func.isRequired,
    }),
    isWishlistRecommendations: PropTypes.bool,
    overlayProductId: PropTypes.string,
    handleOverlayProductIdChange: PropTypes.func,
    setIsPhoneOverlay: PropTypes.func,
    doubleImageWithoutPadding: PropTypes.string,
    doubleImageModifier: PropTypes.string,
    intelistyleWiw: PropTypes.array,
    reducedMode: PropTypes.bool,
    viewSimilarAndShopMore: PropTypes.element,
    details: PropTypes.bool,
    omnibusAlignment: PropTypes.bool,
    onRemove: PropTypes.func,
};

RecommendationsProductList.defaultProps = {
    recommendations: [],
    messages: {},
    heading: '',
    className: '',
    isCarouselVisible: false,
    useDefaultCarouselIndex: true,
};

export default withOptimizely(RecommendationsProductList);
